import styled from 'styled-components';

import { cardBoxShadow, customScroll, flexAllCenter, respondDown } from 'styles/mixins';
import { Breakpoints, COLORS, Z_INDEX } from 'styles/style-constants';

import { StatusDot } from 'pages/amm/components/ConcentratedLiquidity/components/ConcentratedPositionShared.styled';

export const FeedPositionStatusDot = styled(StatusDot)`
    display: inline-block;
    vertical-align: middle;
    margin-right: 0.6rem;
`;

export const BellWrapper = styled.div`
    position: relative;
    margin-left: 0.8rem;
`;

export const BellButton = styled.button`
    ${flexAllCenter};
    position: relative;
    width: 4.8rem;
    height: 4.8rem;
    border: 0.1rem solid ${COLORS.controlBorder};
    border-radius: 0.6rem;
    background: ${COLORS.controlBackground};
    color: ${COLORS.textPrimary};
    cursor: pointer;

    &:hover {
        background: ${COLORS.controlHover};
        color: ${COLORS.purple500};
    }

    svg {
        width: 1.6rem;
        height: 1.6rem;
    }
`;

export const UnreadBadge = styled.div`
    ${flexAllCenter};
    position: absolute;
    top: 0.4rem;
    right: 0.4rem;
    min-width: 1.6rem;
    height: 1.6rem;
    padding: 0 0.4rem;
    border-radius: 0.8rem;
    background: ${COLORS.accentStrong};
    color: ${COLORS.white};
    font-size: 1rem;
    font-weight: 700;
    line-height: 1;
`;

export const Dropdown = styled.div`
    ${cardBoxShadow};
    position: absolute;
    top: 6rem;
    right: 0;
    width: 36rem;
    max-height: 48rem;
    display: flex;
    flex-direction: column;
    background: ${COLORS.surface};
    border-radius: 0.6rem;
    padding: 2.4rem;
    z-index: ${Z_INDEX.accountMenu};

    ${respondDown(Breakpoints.sm)`
        position: fixed;
        top: 5.8rem;
        left: 0;
        right: 0;
        width: 100%;
        border-radius: 0;
    `}
`;

export const DropdownHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 1.6rem;
`;

export const DropdownTitle = styled.h3`
    font-size: 2rem;
    line-height: 2.8rem;
    color: ${COLORS.textPrimary};
`;

export const HeaderActions = styled.div`
    display: flex;
    align-items: center;
    gap: 1.2rem;
`;

export const MarkReadButton = styled.button`
    border: none;
    background: none;
    padding: 0;
    font-size: 1.4rem;
    color: ${COLORS.purple500};
    cursor: pointer;

    &:hover {
        color: ${COLORS.purple400};
    }
`;

export const FeedList = styled.div`
    ${customScroll};
    display: flex;
    flex-direction: column;
    overflow-y: auto;
    // Push the scrollbar toward the dropdown edge instead of hugging the text.
    margin-right: -1.6rem;
    padding-right: 1.6rem;
`;

export const FeedItemWrapper = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0.4rem;
    padding: 1.6rem 0;
    border-bottom: 0.1rem solid ${COLORS.gray100};
`;

export const FeedItemHeader = styled.div`
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 1.2rem;
`;

export const FeedItemTitle = styled.span<{ $unread: boolean }>`
    font-size: 1.4rem;
    font-weight: 700;
    color: ${({ $unread }) => ($unread ? COLORS.textPrimary : COLORS.textGray)};
`;

export const FeedItemTime = styled.span`
    font-size: 1.2rem;
    color: ${COLORS.gray300};
    white-space: nowrap;
`;

export const FeedItemMeta = styled.div`
    display: flex;
    align-items: center;
    gap: 0.8rem;
    flex-shrink: 0;
`;

export const UnreadDot = styled.div`
    width: 0.8rem;
    height: 0.8rem;
    min-width: 0.8rem;
    border-radius: 50%;
    background: ${COLORS.accentStrong};
`;

export const FeedItemSummary = styled.span`
    font-size: 1.4rem;
    line-height: 2rem;
    color: ${COLORS.textGray};

    a {
        color: ${COLORS.purple500};
        text-decoration: underline;
    }
`;

export const FeedItemFields = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0.2rem;
    margin-top: 0.4rem;
`;

export const FeedItemField = styled.div`
    font-size: 1.2rem;
    line-height: 1.8rem;
    color: ${COLORS.textGray};
`;

export const FeedItemFieldLabel = styled.span`
    font-weight: 700;
    color: ${COLORS.textPrimary};
`;

export const FeedItemFieldValue = styled.span<{ $strikethrough?: boolean; $block?: boolean }>`
    display: ${({ $block }) => ($block ? 'block' : 'inline')};
    white-space: pre-line;
    text-decoration: ${({ $strikethrough }) => ($strikethrough ? 'line-through' : 'none')};

    a {
        color: ${COLORS.purple500};
        text-decoration: ${({ $strikethrough }) =>
            $strikethrough ? 'line-through underline' : 'underline'};
    }
`;

export const FeedItemLink = styled.span`
    align-self: flex-start;
    margin-top: 0.4rem;
    font-size: 1.4rem;
    line-height: 2rem;
    color: ${COLORS.purple500};
    cursor: pointer;

    &:hover {
        color: ${COLORS.purple400};
    }
`;

export const EmptyState = styled.div`
    ${flexAllCenter};
    flex-direction: column;
    gap: 1.6rem;
    padding: 3.2rem 0;
    font-size: 1.4rem;
    color: ${COLORS.textGray};
    text-align: center;
`;

export const SettingsLink = styled.div`
    display: flex;
    align-items: center;
    gap: 0.8rem;
    padding-top: 1.6rem;
    font-size: 1.4rem;
    color: ${COLORS.textGray};
    cursor: pointer;

    &:hover {
        color: ${COLORS.purple500};
    }

    svg {
        width: 1.6rem;
        height: 1.6rem;
    }
`;
